<template>
  <div v-if="cities.length" class="nearby-cities">
    <h2 class="nearby-cities__title">{{ $t('Nearby Cities') }}</h2>
    <div class="nearby-cities__grid">
      <nuxt-link
        v-for="city in cities"
        :key="city.slug"
        :to="localePath(`/${props.type || 'hotels'}/${city.slug}`)"
        class="nearby-cities__item"
      >
        <span class="nearby-cities__name">{{ city.name }}</span>
        <span class="nearby-cities__distance">{{ city.distance_km }} km</span>
      </nuxt-link>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  citySlug: string
  type?: 'hotels' | 'experiences'
}>()

const config = useRuntimeConfig()
const localePath = useLocalePath()
const baseUrl = config.public.baseURL

interface NearbyCity {
  name: string
  slug: string
  country_code: string
  distance_km: number
}

const cities = ref<NearbyCity[]>([])

onMounted(async () => {
  try {
    const domain = props.type === 'experiences' ? '&domain=activity' : ''
    const data = await $fetch<NearbyCity[]>(
      `${baseUrl}/api/product/v1/geo-places/${props.citySlug}/nearby?limit=6${domain}`
    )
    cities.value = data || []
  } catch {
    // Silently fail — nearby cities is supplementary
  }
})
</script>

<style scoped>
.nearby-cities {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--light-grey-border, #e0e0e0);
}

.nearby-cities__title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
}

.nearby-cities__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
}

.nearby-cities__item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-radius: 8px;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.15s;
}

.nearby-cities__item:hover {
  background-color: rgba(25, 133, 249, 0.06);
}

.nearby-cities__name {
  font-size: 15px;
  font-weight: 500;
}

.nearby-cities__distance {
  font-size: 13px;
  color: var(--dark-grey);
  white-space: nowrap;
}

@media screen and (max-width: 768px) {
  .nearby-cities__grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
